import {
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Modal, Spin, message } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import createApi from "../../../../api";
import PersonalBoardCardModal, {
  PersonalBoardCardDraft,
  PersonalBoardEditableCard,
} from "../PersonalBoardCardModal";
import PersonalBoardDetailModal from "../PersonalBoardDetailModal";
import "./index.css";

export interface PersonalBoardCard extends PersonalBoardEditableCard {
  created_at: number;
  updated_at: number;
}

export interface PersonalBoardColumn {
  column_id: string;
  cards: PersonalBoardCard[];
  created_at: number;
}

export interface PersonalBoardData {
  columns: PersonalBoardColumn[];
  revision: number;
  updated_at: number;
}

type EditorState =
  | { mode: "create-column" }
  | { mode: "create-card"; columnId: string }
  | { mode: "edit"; card: PersonalBoardCard; columnId: string };

export function formatPersonalBoardCountdown(dueTime: number, now: number) {
  const distance = dueTime - now;
  const totalMinutes = Math.max(1, Math.ceil(Math.abs(distance) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const detail = days
    ? `${days}天 ${hours}小时 ${minutes}分钟`
    : hours
    ? `${hours}小时 ${minutes}分钟`
    : `${minutes}分钟`;
  return {
    label: distance < 0 ? `已逾期 ${detail}` : `剩余 ${detail}`,
    state: distance < 0 ? "overdue" : distance <= 3 * 86400000 ? "warning" : "normal",
  } as const;
}

const PersonalBoardPage: FC = () => {
  const [board, setBoard] = useState<PersonalBoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingCardId, setDeletingCardId] = useState("");
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [detailCard, setDetailCard] = useState<PersonalBoardCard | null>(null);
  const [previewImage, setPreviewImage] = useState("");
  const [now, setNow] = useState(Date.now());

  const loadBoard = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const { data } = await createApi().post("/personal-board/detail", {});
      if (data.code !== 0) {
        message.error(data.message || "获取个人看板失败");
        return;
      }
      setBoard(data.data);
    } catch {
      message.error("获取个人看板失败");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setNow(Date.now());
      interval = window.setInterval(() => setNow(Date.now()), 60000);
    }, 60000 - (Date.now() % 60000) + 20);
    return () => {
      window.clearTimeout(timeout);
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, []);

  const cardCount = useMemo(
    () =>
      (board?.columns || []).reduce(
        (total, column) => total + column.cards.length,
        0
      ),
    [board]
  );

  async function submitCard(draft: PersonalBoardCardDraft) {
    if (!editorState || saving) {
      return;
    }
    setSaving(true);
    try {
      const isEdit = editorState.mode === "edit";
      const { data } = await createApi().post(
        isEdit ? "/personal-board/card/update" : "/personal-board/card/create",
        isEdit
          ? { card_id: editorState.card.card_id, card: draft }
          : {
              card: draft,
              create_column: editorState.mode === "create-column",
              ...(editorState.mode === "create-card"
                ? { column_id: editorState.columnId }
                : {}),
            }
      );
      if (data.code !== 0) {
        message.error(data.message || (isEdit ? "更新卡片失败" : "创建卡片失败"));
        return;
      }
      setBoard(data.data);
      setEditorState(null);
      message.success(isEdit ? "看板卡片已更新" : "看板卡片已创建");
    } catch {
      message.error(editorState.mode === "edit" ? "更新卡片失败" : "创建卡片失败");
    } finally {
      setSaving(false);
    }
  }

  function confirmDeleteCard(card: PersonalBoardCard) {
    Modal.confirm({
      centered: true,
      className: "personalBoardArchiveConfirm",
      title: "确认归档这张卡片？",
      content: (
        <div className="personalBoardArchiveConfirm__content">
          <strong>{card.title || "未命名卡片"}</strong>
          <p>归档后卡片将从个人看板中删除，且无法恢复。</p>
        </div>
      ),
      okText: "确认归档",
      cancelText: "取消",
      maskClosable: false,
      keyboard: false,
      okButtonProps: { danger: true },
      onOk: () => deleteCard(card),
    });
  }

  async function deleteCard(card: PersonalBoardCard) {
    if (deletingCardId) {
      return;
    }
    setDeletingCardId(card.card_id);
    try {
      const { data } = await createApi().post("/personal-board/card/delete", {
        card_id: card.card_id,
      });
      if (data.code !== 0) {
        throw new Error(data.message || "归档卡片失败");
      }
      setBoard(data.data);
      if (detailCard?.card_id === card.card_id) {
        setDetailCard(null);
      }
      message.success("卡片已归档");
    } catch (error) {
      message.error(error instanceof Error ? error.message : "归档卡片失败");
      throw error;
    } finally {
      setDeletingCardId("");
    }
  }

  if (loading) {
    return (
      <section className="personalBoardPage personalBoardPage--loading">
        <Spin size="large" />
        <strong>正在打开你的个人看板</strong>
        <span>只加载当前账号的私人内容</span>
      </section>
    );
  }

  return (
    <section className="personalBoardPage">
      <div className="personalBoardPage__toolbar">
        <div>
          <strong>{board?.columns.length || 0} 列看板</strong>
          <span>{cardCount} 张个人卡片，仅自己可见</span>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => loadBoard(true)}
          title="刷新个人看板"
        >
          刷新
        </Button>
      </div>

      <div className="personalBoardPage__viewport">
        <div className="personalBoardColumns">
          {(board?.columns || []).map((column, columnIndex) => (
            <section className="personalBoardColumn" key={column.column_id}>
              <div className="personalBoardColumn__label">
                <span>看板 {String(columnIndex + 1).padStart(2, "0")}</span>
                <small>{column.cards.length} 张卡片</small>
              </div>
              <div className="personalBoardColumn__cards">
                {column.cards.map((card) => {
                  const countdown = card.due_time
                    ? formatPersonalBoardCountdown(card.due_time, now)
                    : null;
                  return (
                    <article
                      aria-label={`查看${card.title || "未命名"}卡片详情`}
                      className="personalBoardCard"
                      key={card.card_id}
                      onClick={() => setDetailCard(card)}
                      onKeyDown={(event) => {
                        if (
                          event.currentTarget === event.target &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          setDetailCard(card);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {(card.title || countdown) && (
                        <header className="personalBoardCard__header">
                          {card.title ? <h3>{card.title}</h3> : <span />}
                          {countdown && (
                            <span
                              className={`personalBoardCountdown personalBoardCountdown--${countdown.state}`}
                            >
                              <ClockCircleOutlined />
                              {countdown.label}
                            </span>
                          )}
                        </header>
                      )}
                      <div
                        className="personalBoardCard__content"
                        dangerouslySetInnerHTML={{ __html: card.content }}
                      />
                      <footer className="personalBoardCard__actions">
                        <Button
                          className="personalBoardCard__archive"
                          danger
                          disabled={!!deletingCardId}
                          icon={<DeleteOutlined />}
                          loading={deletingCardId === card.card_id}
                          onClick={(event) => {
                            event.stopPropagation();
                            confirmDeleteCard(card);
                          }}
                        >
                          归档
                        </Button>
                        <Button
                          icon={<EditOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditorState({
                              mode: "edit",
                              card,
                              columnId: column.column_id,
                            });
                          }}
                        >
                          编辑
                        </Button>
                        <Button
                          icon={<PlusOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditorState({
                              mode: "create-card",
                              columnId: column.column_id,
                            });
                          }}
                          type="primary"
                        >
                          新增
                        </Button>
                      </footer>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

          <button
            className="personalBoardAddColumn"
            onClick={() => setEditorState({ mode: "create-column" })}
            type="button"
          >
            <span><PlusOutlined /></span>
            <strong>增加一列看板</strong>
            <small>创建新的个人卡片分区</small>
          </button>
        </div>
      </div>

      <PersonalBoardCardModal
        card={editorState?.mode === "edit" ? editorState.card : undefined}
        mode={editorState?.mode || "create-card"}
        onCancel={() => setEditorState(null)}
        onSubmit={submitCard}
        open={!!editorState}
        saving={saving}
      />

      <PersonalBoardDetailModal
        card={detailCard}
        countdown={
          detailCard?.due_time
            ? formatPersonalBoardCountdown(detailCard.due_time, now)
            : null
        }
        onClose={() => setDetailCard(null)}
        onImageClick={setPreviewImage}
      />

      <Modal
        centered
        className="taskImagePreviewModal"
        destroyOnClose
        footer={null}
        keyboard={false}
        maskClosable={false}
        onCancel={() => setPreviewImage("")}
        open={!!previewImage}
        width="92vw"
      >
        {previewImage && <img alt="看板图片预览" src={previewImage} />}
      </Modal>
    </section>
  );
};

export default PersonalBoardPage;
